import { useMemo } from 'react';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { Assert } from '@cotera/utilities';
import type {
  DatasetTriggerCriterion,
  DatasetTriggerRunMode,
} from '@cotera/sdk/core';
import { useAgentsQuery } from '@cotera/client/app/hooks/use-agents-display-names';
import { useResourceUpdatedWatchable } from '../watchables/use-resource-updated-watchable';
import type {
  TriggerWorkflowsMapState,
  TriggerWorkflowsState,
} from '../watchables/watchables-factory';
import {
  useWorkflowSummaries,
  type WorkflowSummary,
} from '../workflow/use-workflow-summaries';
import type { FolderScope } from '../resources/list/resource-list-types';

export type AgentOption = {
  id: string;
  agentKey: string;
  title: string | null;
  model: string;
  description: string | null;
};

export function useAgentOptions(): AgentOption[] {
  // Reuse the shared agents query rather than defining a second `queryFn` on the
  // same `queryKeys.agents.all` key — React Query dedupes by key, so a divergent
  // query here would never run and we'd read whichever shape happened to be
  // cached first (which is how model/description silently went missing).
  const { data } = useAgentsQuery();
  return useMemo(
    () =>
      (data?.agents ?? []).map((a) => ({
        id: a.id,
        agentKey: a.agentKey,
        title: a.title,
        model: a.model,
        description: a.description,
      })),
    [data?.agents]
  );
}

export type WorkflowTriggerOption = {
  id: string;
  name: string;
  kind: 'cron' | 'webhook' | 'event' | 'dataset' | string;
  schedule: string | null;
  /** Free-form listen name when `kind` is `event`. */
  eventName?: string | null;
  /** Watched dataset when `kind` is `dataset`. */
  datasetId?: string | null;
  /** What the dataset trigger watches for when `kind` is `dataset`. */
  criterion?: DatasetTriggerCriterion | null;
  /** Whether the dataset trigger fires on its own, or only when asked. */
  runMode?: DatasetTriggerRunMode | null;
};

/**
 * Existing org triggers, for the builder's "use existing trigger" picker.
 * Triggers are shared resources, so a workflow can bind to one that already
 * exists instead of minting a duplicate. Backed by a `ResourceUpdated` event
 * watchable so a newly created/bound trigger streams into the picker live.
 */
export function useWorkflowTriggerOptions(): WorkflowTriggerOption[] {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  return useResourceUpdatedWatchable(orgId, (factory) =>
    factory.makeWorkflowTriggerOptionsWatchable({ client })
  );
}

export type WorkflowOption = {
  id: string;
  name: string;
  folderName?: string | null;
  folderScope?: FolderScope | null;
};

export type DatasetOption = {
  id: string;
  name: string;
  folderName?: string | null;
  folderScope?: FolderScope | null;
};

/** Stable empty value for the disabled state — no fetch, no re-subscription. */
const EMPTY_DATASET_OPTIONS: DatasetOption[] = [];

/**
 * Org datasets, for the "watch a dataset" trigger picker. Backed by a
 * `ResourceUpdated` stream so a dataset created in another tab shows up without
 * reopening the wizard.
 */
export function useDatasetOptions(enabled = true): DatasetOption[] {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  return useResourceUpdatedWatchable(
    orgId,
    (factory) => factory.makeDatasetOptionsWatchable({ client }),
    { enabled, disabledValue: EMPTY_DATASET_OPTIONS }
  );
}

/** A workflow bound to a dataset through one of its automation triggers. */
export type DatasetWorkflowOption = {
  id: string;
  name: string;
};

/**
 * The workflows bound to a dataset through its automation triggers — the set the
 * "Run Dataset Workflows" node can target.
 *
 * Deduped across triggers: the same workflow can be bound to more than one
 * trigger on a dataset, but the node picks workflows, not (trigger, workflow)
 * pairs, so a workflow should offer itself once. Disabled with no `datasetId`,
 * where there is nothing to fetch.
 */
export function useDatasetWorkflowOptions(datasetId: string | null): {
  options: DatasetWorkflowOption[];
  isLoading: boolean;
} {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.workflow.datasetAutomationWorkflows({
      orgId,
      datasetId: datasetId ?? '',
    }),
    enabled: datasetId !== null,
    queryFn: async (): Promise<DatasetWorkflowOption[]> => {
      const res = await client.resource.workflow.datasetAutomations({
        datasetId: datasetId ?? '',
      });
      const value = Assert.assertOk(res);
      const seen = new Map<string, DatasetWorkflowOption>();
      for (const trigger of value.triggers) {
        for (const workflow of trigger.workflows) {
          if (!seen.has(workflow.id)) {
            seen.set(workflow.id, { id: workflow.id, name: workflow.name });
          }
        }
      }
      return [...seen.values()];
    },
  });
  return {
    options: data ?? EMPTY_DATASET_WORKFLOW_OPTIONS,
    isLoading: datasetId !== null && isLoading,
  };
}

/** Stable empty value so the disabled/loading state keeps a stable reference. */
const EMPTY_DATASET_WORKFLOW_OPTIONS: DatasetWorkflowOption[] = [];

export type DatasetShape = {
  columns: string[];
  /** The columns that identify a row, resolved the same way the server does. */
  keyColumns: string[];
  /** True when identity falls back to an internal column rather than a business key. */
  keyIsInternal: boolean;
  /**
   * The internal column rows are addressed by when written, or null when the
   * artifact predates durable row ids.
   *
   * Deliberately separate from {@link keyColumns}, which a declared business key
   * takes over: the two answer different questions. A declared key is what a
   * *trigger* matches rows on across re-imports; this is what an *edit* targets,
   * and no declared key substitutes for it. A dataset can therefore have a
   * perfectly good business key and still be unwritable.
   */
  rowIdColumn: string | null;
  /**
   * The artifact the columns above were read from, or null for a dataset that
   * has none yet.
   *
   * Carried so an editor that writes an operation can declare which version of
   * the schema it was authored against — the same claim the grid makes — rather
   * than recording the edit against no artifact at all.
   */
  artifactId: string | null;
};

/**
 * The dataset's current columns and resolved row identity.
 *
 * Row identity is resolved through the same SDK helper the evaluator uses, so
 * what the config form warns about is exactly what the trigger will do. Backed
 * by the `ResourceUpdated` stream rather than a one-shot query because the
 * dataset is editable from another tab while the wizard is open — adding a
 * column there has to reach the field pickers here.
 */
export function useDatasetShape(datasetId: string | null): DatasetShape | null {
  const client = useTenantedClient();
  return useResourceUpdatedWatchable(
    datasetId ?? 'none',
    (factory) => factory.makeDatasetShapeWatchable({ client, datasetId }),
    { enabled: datasetId !== null, disabledValue: null }
  );
}

/** Stable empty value for the disabled state — no fetch, no re-subscription. */
const EMPTY_WORKFLOW_OPTIONS: WorkflowOption[] = [];

/**
 * Org workflows, for the "attach a workflow to this trigger" picker. Backed by
 * a `ResourceUpdated` event watchable so created/renamed workflows stream in.
 * Pass `enabled: false` to skip the fetch/subscription entirely.
 */
export function useWorkflowOptions(enabled = true): WorkflowOption[] {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  return useResourceUpdatedWatchable(
    orgId,
    (factory) => factory.makeWorkflowOptionsWatchable({ client }),
    { enabled, disabledValue: EMPTY_WORKFLOW_OPTIONS }
  );
}

/**
 * Standalone workflows (no trigger bound), filtered server-side so the set is
 * complete rather than derived from the page-limited workflow rollup. Backed by
 * a `ResourceUpdated` watchable so attaching/detaching a trigger restreams the
 * set. Pass `enabled: false` to skip the fetch/subscription entirely.
 */
export function useUnattachedWorkflowOptions(enabled = true): WorkflowOption[] {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  return useResourceUpdatedWatchable(
    `${orgId}:unattached-workflows`,
    (factory) => factory.makeUnattachedWorkflowsWatchable({ client }),
    { enabled, disabledValue: EMPTY_WORKFLOW_OPTIONS }
  );
}

/**
 * Page size of `makeWorkflowOptionsWatchable`. When the loaded list hits this,
 * the org may have more workflows than we've fetched, so derived counts are a
 * lower bound (surfaced as "N+"), not exact.
 */
export const WORKFLOW_OVERVIEW_LIMIT = 100;

export type WorkflowsOverview = {
  /** Every org workflow (first page). */
  workflows: WorkflowOption[];
  /** Per-workflow trigger set + last run, keyed by workflow id. */
  summaries: Map<string, WorkflowSummary>;
  /**
   * Workflows with no trigger bound, fetched server-side (not derived from the
   * summaries), so the standalone set is complete rather than limited to the
   * first page of the full workflow list.
   */
  unattached: WorkflowOption[];
  /** True when the loaded list is at the page cap, so counts undercount the org. */
  capped: boolean;
  /** True when the standalone list is at the page cap (its count undercounts). */
  unattachedCapped: boolean;
};

/**
 * Rolls up the org's workflows with their trigger/run summaries and derives the
 * unattached set. Shared by the page (summary counts + standalone banner) and
 * the accordion (attach picker + standalone section) so they read from a single
 * set of watchables. Pass `enabled: false` to skip fetching the workflow list +
 * summaries entirely.
 */
export function useWorkflowsOverview(enabled = true): WorkflowsOverview {
  const workflows = useWorkflowOptions(enabled);
  const unattached = useUnattachedWorkflowOptions(enabled);
  // Summaries drive the last-run labels for both the trigger-derived rows and
  // the standalone rows, so cover both id sets — a standalone workflow can fall
  // past the full list's first page and otherwise miss its summary.
  const ids = useMemo(() => {
    const set = new Set<string>();
    for (const w of workflows) {
      set.add(w.id);
    }
    for (const w of unattached) {
      set.add(w.id);
    }
    return [...set];
  }, [workflows, unattached]);
  const { summaries } = useWorkflowSummaries(ids, enabled);
  return {
    workflows,
    summaries,
    unattached,
    capped: workflows.length >= WORKFLOW_OVERVIEW_LIMIT,
    unattachedCapped: unattached.length >= WORKFLOW_OVERVIEW_LIMIT,
  };
}

/**
 * One bound workflow's contribution to a trigger's status bars: the latest run
 * status through this trigger (the bar's colour) and how many of its runs in the
 * 24h before that run failed (the bar's hover tooltip).
 */
export type TriggerWorkflowBar = {
  workflowId: string;
  name: string;
  lastRunStatus: string | null;
  failedRuns: number;
  totalRuns: number;
};

export type TriggerSummary = {
  workflowCount: number;
  lastFiredAt: Date | null;
  hasError: boolean;
  workflows: TriggerWorkflowBar[];
};

/**
 * Batched trigger rollups plus whether the first fetch has landed. `loaded` lets
 * a row draw its status cell as a shimmer until the rollup arrives — the map is
 * empty both before the fetch and (legitimately) for a trigger with no runs, so
 * emptiness alone can't tell "still loading" from "nothing to show". Set true
 * once the request resolves, success or error, so a failed fetch settles to an
 * empty state rather than shimmering forever.
 */
export type TriggerSummariesState = {
  summaries: Map<string, TriggerSummary>;
  loaded: boolean;
};

/**
 * Batch per-trigger rollups (count, last fired, error state) for accordion +
 * picker. Backed by a `ResourceUpdated` event watchable so a fire or binding
 * change streams into the rollups without a manual refetch. Loads independently
 * of the page — the rows paint immediately and each status cell shimmers until
 * this resolves.
 */
export function useTriggerSummaries(
  triggerIds: string[]
): TriggerSummariesState {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const idsKey = useMemo(() => [...triggerIds].sort().join(','), [triggerIds]);

  return useResourceUpdatedWatchable(`${orgId}:${idsKey}`, (factory) =>
    factory.makeTriggerSummariesWatchable({ client, triggerIds })
  );
}

export type TriggerWorkflow = {
  workflowId: string;
  name: string;
  status: string;
  bindingEnabled: boolean;
  lastRunAt: Date | null;
  lastRunStatus: string | null;
  folderName?: string | null;
  folderScope?: FolderScope | null;
};

/** Stable empty value for collapsed (disabled) rows — no fetch, no spinner. */
const EMPTY_TRIGGER_WORKFLOWS_STATE: TriggerWorkflowsState = {
  workflows: [],
  loaded: true,
};

/**
 * Workflows bound to a trigger, for the group-by-trigger view. Lazily enabled
 * (only when a row is expanded) to avoid a request per trigger up front: while
 * collapsed the hook holds a static empty value with no event subscription.
 *
 * Once expanded it streams binding/run changes via a `ResourceUpdated`
 * watchable, and reads/writes the React Query cache so collapse→re-expand shows
 * the last loaded list immediately instead of flashing a loading state.
 */
export function useTriggerWorkflows(
  triggerId: string,
  enabled: boolean
): { workflows: TriggerWorkflow[]; isLoading: boolean } {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const active = enabled && triggerId !== '';

  const state = useResourceUpdatedWatchable(
    // Keyed on the trigger only (not `active`) — the `enabled` flag drives the
    // subscribe/unsubscribe so the factory's cache survives collapse/re-expand.
    `${orgId}:${triggerId}`,
    (factory) =>
      factory.makeTriggerWorkflowsWatchable({ client, orgId, triggerId }),
    { enabled: active, disabledValue: EMPTY_TRIGGER_WORKFLOWS_STATE }
  );

  return useMemo(
    () => ({ workflows: state.workflows, isLoading: active && !state.loaded }),
    [state.workflows, state.loaded, active]
  );
}

const EMPTY_TRIGGER_WORKFLOWS_MAP_STATE: TriggerWorkflowsMapState = {
  workflowsByTriggerId: new Map(),
  loaded: true,
};

/**
 * Batch-loads bound workflows for many triggers (e.g. Library folder rows that
 * are filed `workflow_trigger`s). Shares the same React Query keys as
 * {@link useTriggerWorkflows} so accordion expand and Library stay in sync.
 */
export function useTriggerWorkflowsMap(triggerIds: string[]): {
  workflowsByTriggerId: Map<string, TriggerWorkflow[]>;
  isLoading: boolean;
} {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const stableIds = useMemo(
    () => [...new Set(triggerIds.filter((id) => id !== ''))].sort(),
    [triggerIds]
  );
  const key = stableIds.join('|');
  const enabled = stableIds.length > 0;

  const state = useResourceUpdatedWatchable(
    `${orgId}:trigger-workflows-map:${key}`,
    (factory) =>
      factory.makeTriggerWorkflowsMapWatchable({
        client,
        orgId,
        triggerIds: stableIds,
      }),
    { enabled, disabledValue: EMPTY_TRIGGER_WORKFLOWS_MAP_STATE }
  );

  return {
    workflowsByTriggerId: state.workflowsByTriggerId,
    isLoading: enabled && !state.loaded,
  };
}

/** Scheduled-agent bundle mapped for the trigger detail UI. */
export type ScheduledWorkflowDetail = {
  id: string;
  orgId: string;
  ownerId: string;
  name: string;
  status: 'active' | 'archived';
  workflowStatus?: string;
  createdAt: Date;
  updatedAt: Date;
  schedule?: string;
  timezone?: string;
  cronTriggerId?: string;
  agentId?: string | null;
  agentTitle?: string | null;
  prompt?: string;
  /** Persisted trigger row id (`ResourceType.WorkflowTrigger` entity id). */
  triggerResourceId: string;
};

/** Loads a scheduled-agent workflow + trigger via `workflow.getScheduledAgentBundle`. */
export function useScheduledWorkflow(workflowId: string) {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  return useQuery({
    queryKey: queryKeys.workflow.scheduledAgentBundle({
      orgId,
      workflowId,
    }),
    enabled: workflowId !== '',
    queryFn: async (): Promise<ScheduledWorkflowDetail> => {
      const res = await client.resource.workflow.getScheduledAgentBundle({
        workflowId,
      });
      const bundle = Assert.assertOk(res);
      const w = bundle.workflow;
      const t = bundle.trigger;
      return {
        id: w.id,
        orgId: w.orgId,
        ownerId: w.ownerId,
        name: w.name,
        status: w.status,
        workflowStatus: w.workflowStatus,
        createdAt:
          w.createdAt instanceof Date
            ? w.createdAt
            : new Date(w.createdAt as string),
        updatedAt:
          w.updatedAt instanceof Date
            ? w.updatedAt
            : new Date(w.updatedAt as string),
        schedule:
          w.schedule !== undefined && w.schedule !== ''
            ? w.schedule
            : t.kind === 'cron'
            ? t.schedule
            : undefined,
        timezone:
          w.timezone !== undefined && w.timezone !== ''
            ? w.timezone
            : t.kind === 'cron'
            ? t.timezone
            : undefined,
        cronTriggerId: t.kind === 'cron' ? t.id : w.cronTriggerId,
        agentId: w.agentId,
        agentTitle: w.agentTitle,
        prompt: w.prompt,
        triggerResourceId: t.id,
      };
    },
  });
}
